import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { changeDeal } from 'bundles/apex/deals/form/store/deal/actions';

import AddressFields from 'bundles/apex/deals/form/fields/address_fields';
import AgreementType from 'bundles/apex/deals/form/fields/agreement_type';
import Area from 'bundles/apex/deals/form/fields/area';
import CalculationMode from 'bundles/apex/deals/form/fields/calculation_mode';
import CriticalDate from 'bundles/apex/deals/form/fields/critical_date';
import CancelButton from 'bundles/apex/deals/form/cancel_button';
import Contacts from 'bundles/apex/deals/form/fields/contacts';
import Currency from 'bundles/apex/deals/form/fields/currency';
import CustomFields from 'bundles/apex/deals/form/fields/custom_fields';
import DealType from 'bundles/apex/deals/form/fields/deal_type';
import Errors from 'bundles/apex/deals/form/errors';
import GrossCommission from 'bundles/apex/deals/form/fields/gross_commission';
import InternalBrokers from 'bundles/apex/deals/form/fields/internal_brokers';
import LeaseStructure from 'bundles/apex/deals/form/fields/lease_structure';
import LeaseRate from 'bundles/apex/deals/form/fields/lease_rate';
import LeaseTerm from 'bundles/apex/deals/form/fields/lease_term';
import LeaseType from 'bundles/apex/deals/form/fields/lease_type';
import ManagedInput from 'components/form/managed_input';
import Numeric from 'bundles/apex/deals/form/fields/numeric';
import OutsideBrokers from 'bundles/apex/deals/form//fields/outside_brokers';
import Percentage from 'bundles/apex/deals/form/fields/percentage';
import Property from 'bundles/apex/deals/form/fields/property';
import PropertyType from 'bundles/apex/deals/form/fields/property_type';
import SaveButton from 'bundles/apex/deals/form/save_button';
import Section from './section';
import SpaceSize from 'bundles/apex/deals/form/fields/space_size';
import Stage from 'bundles/apex/deals/form/fields/stage';
import Text from 'bundles/apex/deals/form/fields/text';
import TextArea from 'bundles/apex/deals/form/fields/textarea';
import TransactionSideSubsection from './transaction_side_subsection';
import TransactionValue from 'bundles/apex/deals/form/fields/transaction_value';
import { BUY_SIDE, SELL_SIDE } from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { getPropertySectionLabel } from 'bundles/apex/deals/helpers/labels';

export function DealForm({ changeDeal, deal, disableIdentifier, transactionSidesEnabled, onCancel, onSave }) {
  const criticalDates = (
    <Section label="Critical Dates">
      <CriticalDate name="listingExecutionDate" />
      <CriticalDate name="listingExpirationDate" />
      <CriticalDate name="contractExecutionDate" />
      <CriticalDate name="closeDate" />
      <CriticalDate name="leaseStartDate" />
      <CriticalDate name="leaseExpirationDate" />
      <CriticalDate name="renewalOptionNoticeDate" />
      <CustomFields forDates />
    </Section>
  );

  return (
    <div>
      <Errors className="mb-3" />

      <Section label="Deal">
        <CalculationMode/>
        <DealType/>
        <Stage/>
        <Text name="name" />
        <Text disabled={disableIdentifier} name="identifier" />
        <Text name="deadReason" />
        <Percentage name="closeProbability" />
      </Section>

      { !transactionSidesEnabled &&
        <>
          <Section label="Brokers">
            <InternalBrokers/>
            <OutsideBrokers/>
          </Section>

          <Contacts/>
          {criticalDates}
        </> }

      <Section label={getPropertySectionLabel(deal)}>
        <Property/>
        <PropertyType/>
        <TextArea name="requirements" />
        <Text name="propertyName" />
        <AddressFields/>
        <Numeric name="buildingSize" />
        <Area name="lotSize" />
        <SpaceSize/>
        <Numeric name="numberOfUnits" />
        <Percentage name="occupancyPercent" />
        <Numeric name="yearBuilt" />
        <CustomFields category="property" />
      </Section>

      <Section label="Transaction">
        <AgreementType />
        <LeaseType/>
        <LeaseRate/>
        <LeaseTerm/>
        <TransactionValue/>
        {!deal.hasTransactionSides && <GrossCommission/>}
        <LeaseStructure modalEditing />
        <Numeric name="capRate" />
        <Currency name="netOperatingIncome" />
        <CustomFields category="financials" />
      </Section>

      { transactionSidesEnabled &&
        <>
          {criticalDates}
          <Section label="Brokers">
            { deal.hasTransactionSides
              ? (
                <>
                  <TransactionSideSubsection deal={deal} transactionSide={SELL_SIDE} />
                  <TransactionSideSubsection deal={deal} transactionSide={BUY_SIDE} />
                </>
              ) : (
                <>
                  <InternalBrokers/>
                  <OutsideBrokers/>
                </>
              )}
          </Section>

          <Contacts/>
        </> }

      <Section label="Custom Fields">
        <CustomFields category={null} />
      </Section>

      {deal.canActivateVoucher &&
        <ManagedInput
          label="Create voucher"
          type="checkbox"
          value={deal.activateVoucher}
          wrapperClassName="d-flex justify-content-end mb-3"
          onValueChange={activateVoucher => changeDeal({ activateVoucher })}
        />
      }

      {deal.canCloseListing && deal.stage === 'closed' &&
        <ManagedInput
          label="Close listing"
          name="closeListing"
          type="checkbox"
          value={deal.closeListing}
          wrapperClassName="d-flex justify-content-end mb-3"
          onChange={changeDeal}
        />
      }

      <div className="text-end">
        <CancelButton className="me-2" onCancel={onCancel} />
        <SaveButton onSave={onSave} />
      </div>
    </div>
  );
}

DealForm.propTypes = {
  changeDeal: PropTypes.func.isRequired,
  deal: PropTypes.shape({
    canActivateVoucher: PropTypes.bool,
    canCloseListing: PropTypes.bool,
    closeListing: PropTypes.bool,
    stage: PropTypes.string.isRequired,
  }).isRequired,
  disableIdentifier: PropTypes.bool,
  transactionSidesEnabled: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  deal: state.deal,
  disableIdentifier: state.features?.moCustomDealIds,
  transactionSidesEnabled: state.features.transactionSideDetails,
});

const mapDispatchToProps = {
  changeDeal,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealForm);
